import { useEffect, useState } from "react";
import { BellRing, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import CircularProgress from "@/components/common/CircularProgress";
import { Textarea } from "@/components/ui/textarea";
import { comunidadAdapter, RecordatorioGrupo, RecordatorioPreview } from "@/hooks/api/comunidad";
import { toastUtils } from "@/hooks/useToast";

interface Props {
  formacionKey: string;
  edition: number;
  onClose: () => void;
}

/**
 * Panel de admin → inscriptos: "Avisar antes de esta edición". El backend arma solo los grupos
 * (anotadas, fueron un rato, no fueron, no se anotaron a ninguna) con el texto de cada uno; acá
 * se revisan los nombres, se destilda a quien no corresponda (el docente, cuentas internas) y se
 * manda con un click. A cada persona le llega una sola vez por edición.
 */
export const FormacionRecordatorioPanel = ({ formacionKey, edition, onClose }: Props) => {
  const [preview, setPreview] = useState<RecordatorioPreview | null>(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [open, setOpen] = useState<Set<string>>(new Set());
  const [sending, setSending] = useState(false);
  // Texto de cada grupo, editable: arranca con el propuesto; si no se toca, sale ese.
  const [textos, setTextos] = useState<Partial<Record<RecordatorioGrupo, { title: string; message: string }>>>({});

  const load = async () => {
    setLoading(true);
    try {
      const data = await comunidadAdapter.getRecordatorioPreview(formacionKey, edition);
      setPreview(data);
      setTextos(Object.fromEntries(data.grupos.map((g) => [g.grupo, { title: g.title, message: g.message }])));
      setSelected(
        new Set(data.grupos.flatMap((g) => g.personas.filter((p) => !p.yaAvisada).map((p) => p.psychologistId))),
      );
    } catch {
      setPreview(null);
      toastUtils.error("No se pudieron armar los grupos. Fijate que la edición tenga fecha y probá de nuevo.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formacionKey, edition]);

  const toggle = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const toggleGroup = (ids: string[], on: boolean) =>
    setSelected((prev) => {
      const next = new Set(prev);
      ids.forEach((id) => (on ? next.add(id) : next.delete(id)));
      return next;
    });

  const toggleOpen = (grupo: string) =>
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(grupo)) next.delete(grupo);
      else next.add(grupo);
      return next;
    });

  const total = selected.size;

  const handleSend = async () => {
    if (total === 0) return;
    const ok = window.confirm(
      `Se le va a mandar el recordatorio a ${total} ${total === 1 ? "psicóloga" : "psicólogas"} (campanita y celular), cada una con el texto de su grupo (tal como está escrito acá). ¿Enviar?`,
    );
    if (!ok) return;
    setSending(true);
    try {
      // Solo se mandan los textos que se cambiaron (el resto lo arma el backend igual).
      const editados = (preview?.grupos ?? [])
        .map((g) => ({ grupo: g.grupo, ...(textos[g.grupo] ?? { title: g.title, message: g.message }) }))
        .map((t) => ({ ...t, title: t.title.trim(), message: t.message.trim() }))
        .filter((t) => {
          const g = preview?.grupos.find((x) => x.grupo === t.grupo);
          return g && (t.title !== g.title || t.message !== g.message);
        });
      if (editados.some((t) => !t.title || !t.message)) {
        toastUtils.error("Hay un título o un mensaje vacío. Completalo o tocá «Volver al texto original».");
        return;
      }
      const avisados = await comunidadAdapter.sendRecordatorio(formacionKey, edition, Array.from(selected), editados);
      toastUtils.success(`Listo: se está mandando el recordatorio a ${avisados}.`);
      await load();
    } catch {
      toastUtils.error("No se pudo mandar el recordatorio. Probá de nuevo.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="flex flex-col gap-3 rounded-xl border border-[#C9BCE8] bg-[#F5F1FF] p-4">
      <span className="text-sm font-bold text-[#2D2049]">Avisar antes de la {edition}ª edición</span>
      <span className="text-xs text-gray-500">
        Revisá los grupos y destildá a quien no corresponda (por ejemplo, quien da la formación). Quien ya hizo una
        edición completa no aparece. A cada persona le llega una sola vez.
      </span>

      {loading ? (
        <div className="flex justify-center py-6">
          <CircularProgress />
        </div>
      ) : preview ? (
        <>
          {preview.grupos.map((g) => {
            const pendientes = g.personas.filter((p) => !p.yaAvisada);
            const elegidas = pendientes.filter((p) => selected.has(p.psychologistId)).length;
            const isOpen = open.has(g.grupo);
            return (
              <div key={g.grupo} className="flex flex-col gap-2 rounded-lg bg-white p-3" data-grupo={g.grupo}>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <label className="flex items-center gap-2 text-[13px] font-semibold text-[#2D2049]">
                    <input
                      type="checkbox"
                      aria-label={`Todo el grupo: ${g.descripcion}`}
                      checked={pendientes.length > 0 && elegidas === pendientes.length}
                      disabled={pendientes.length === 0}
                      onChange={(e) =>
                        toggleGroup(
                          pendientes.map((p) => p.psychologistId),
                          e.target.checked,
                        )
                      }
                    />
                    {g.descripcion}
                  </label>
                  <span className="text-xs text-gray-500">
                    {elegidas} de {g.personas.length}
                  </span>
                </div>
                {/* Texto editable: si querés decir algo distinto para este grupo. */}
                <div className="flex flex-col gap-1.5 rounded-md bg-[#FAF8FF] px-3 py-2 text-[13px]">
                  <input
                    aria-label={`Título del aviso: ${g.descripcion}`}
                    className="w-full rounded-md border border-[#E3D9FF] bg-white px-2 py-1 font-semibold text-[#2D2049]"
                    maxLength={120}
                    value={textos[g.grupo]?.title ?? g.title}
                    onChange={(e) =>
                      setTextos((prev) => ({
                        ...prev,
                        [g.grupo]: { title: e.target.value, message: prev[g.grupo]?.message ?? g.message },
                      }))
                    }
                  />
                  <Textarea
                    aria-label={`Mensaje del aviso: ${g.descripcion}`}
                    className="min-h-[60px] bg-white text-[13px] text-[#4A3D73]"
                    maxLength={500}
                    value={textos[g.grupo]?.message ?? g.message}
                    onChange={(e) =>
                      setTextos((prev) => ({
                        ...prev,
                        [g.grupo]: { title: prev[g.grupo]?.title ?? g.title, message: e.target.value },
                      }))
                    }
                  />
                  {(textos[g.grupo]?.title !== g.title || textos[g.grupo]?.message !== g.message) && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="self-start text-[#665090]"
                      onClick={() => setTextos((prev) => ({ ...prev, [g.grupo]: { title: g.title, message: g.message } }))}
                    >
                      <RotateCcw size={14} className="mr-1.5" />
                      Volver al texto original
                    </Button>
                  )}
                </div>
                {g.personas.length > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="self-start text-[#665090]"
                    onClick={() => toggleOpen(g.grupo)}
                  >
                    {isOpen ? "Ocultar lista" : `Ver lista (${g.personas.length})`}
                  </Button>
                )}
                {isOpen && (
                  <div className="flex max-h-[240px] flex-col gap-1 overflow-y-auto">
                    {g.personas.map((p) => (
                      <label key={p.psychologistId} className="flex items-center gap-2 text-[13px] text-[#2D2049]">
                        <input
                          type="checkbox"
                          checked={!p.yaAvisada && selected.has(p.psychologistId)}
                          disabled={p.yaAvisada}
                          onChange={() => toggle(p.psychologistId)}
                        />
                        <span>{`${p.name} ${p.surname}`.trim() || p.email}</span>
                        <span className="text-xs text-gray-400">{p.email}</span>
                        {p.nota && <span className="text-xs text-gray-500">· {p.nota}</span>}
                        {p.yaAvisada && <span className="text-xs text-[#665090]">· ya avisada</span>}
                      </label>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
          <div className="flex justify-end gap-2">
            <Button variant="outline" size="sm" onClick={onClose} disabled={sending}>
              Cerrar
            </Button>
            <Button
              size="sm"
              className="bg-[#665090] hover:bg-[#665090]/90"
              onClick={handleSend}
              disabled={sending || total === 0}
            >
              <BellRing size={15} className="mr-1.5" />
              {sending ? "Enviando…" : `Mandar a ${total}`}
            </Button>
          </div>
        </>
      ) : (
        <div className="flex justify-end">
          <Button variant="outline" size="sm" onClick={onClose}>
            Cerrar
          </Button>
        </div>
      )}
    </div>
  );
};

export default FormacionRecordatorioPanel;
